/**
 * Structure-split timber/CNC awards are one approved quote per structure set.
 * Forecast AP used to keep a single bill per supplier, so the second (and later)
 * approved quotes never showed. This lists those missing quote totals.
 */

import type { RfqProject, RfqSubmission } from '../types';
import { approvedSubmissionIdsOnRfqProject } from './rfqApprovedQuoteCoverage';
import { submissionQuoteTotal } from './rfqQuoteTotal';
import type { SupplierBill } from './supplierBills';

/** Award fields the forecast and billing summary both have on an RFQ project. */
export type AwardedQuoteForecastProject = {
  id?: string;
  workType?: RfqProject['workType'];
  dealId?: string;
  timberProjectId?: string;
  approvedSubmissionIds?: string[];
  approvedSubmissionIdsByRelease?: Record<string, string[] | undefined>;
};

export type AwardedQuoteForecastGaps = {
  slices: AwardedQuoteForecastSlice[];
  /**
   * Untouched pending bills whose amount no longer matches the approved quote.
   * A bill that already has a schedule, approval, or payment stays on the page.
   */
  omitBillIds: string[];
};

export type AwardedQuoteForecastSlice = {
  submissionId: string;
  amount: number;
  vendorName: string;
  label: string;
  dealId: string;
  projectId?: string;
  structureNames: string[];
};

function amountsMatch(a: number, b: number): boolean {
  return Math.abs(Math.round(a) - Math.round(b)) < 1;
}

function isStructureSubmissionKey(id: string | undefined): boolean {
  const key = (id || '').trim();
  return key.length > 0 && !key.startsWith('org:');
}

function awardCategory(workType: string | undefined): 'timber' | 'cnc' | null {
  if (workType === 'timber_rfq') return 'timber';
  if (workType === 'cnc_quote') return 'cnc';
  return null;
}

function coversAward(bill: SupplierBill): boolean {
  return bill.status !== 'void' && (Number(bill.amount) || 0) > 0
    && (bill.category === 'timber' || bill.category === 'cnc');
}

/**
 * A bill already in Accounts Payable covers its quote even when later invoices
 * raised the total past the original award. Only an untouched pending shell
 * (no schedule, no approval, no payment) is treated as stale when the dollars
 * no longer match — that is the case a PM quote edit should replace.
 */
function liveAwardBillCoversQuote(bill: SupplierBill): boolean {
  if (bill.status === 'paid' || bill.status === 'ready_to_pay' || bill.status === 'entered_in_qb') {
    return true;
  }
  if (Object.keys(bill.installmentPayments ?? {}).length > 0) return true;
  if (Object.keys(bill.installmentApprovals ?? {}).length > 0) return true;
  if ((bill.paymentTerms?.installments?.length ?? 0) > 0) return true;
  return false;
}

/**
 * Approved structure quotes whose dollars are not already on a supplier bill.
 * An org-level bill that equals the sum of the unbilled quotes counts as covering
 * them (one lump award). A bill that matches one quote's amount covers only that quote.
 */
export function awardedQuotesMissingFromBills(input: {
  rfqProjects: AwardedQuoteForecastProject[];
  rfqSubmissions: RfqSubmission[];
  supplierBills: SupplierBill[];
}): AwardedQuoteForecastGaps {
  const projectById = new Map<string, AwardedQuoteForecastProject>();
  const approvedIds = new Set<string>();
  for (const project of input.rfqProjects) {
    if (project.id) projectById.set(project.id, project);
    approvedSubmissionIdsOnRfqProject(project as RfqProject).forEach((id) => approvedIds.add(id));
  }
  if (!approvedIds.size) return { slices: [], omitBillIds: [] };

  const vendorByOrg = new Map<string, string>();
  for (const bill of input.supplierBills) {
    if (bill.supplierOrgId && bill.vendorName.trim()) {
      vendorByOrg.set(bill.supplierOrgId, bill.vendorName.trim());
    }
  }

  type Group = {
    category: 'timber' | 'cnc';
    project: AwardedQuoteForecastProject;
    orgId: string;
    subs: RfqSubmission[];
  };
  const groups = new Map<string, Group>();
  for (const sub of input.rfqSubmissions) {
    if (!sub.id || !approvedIds.has(sub.id) || sub.notBidding || sub.rejectedAt) continue;
    if (!sub.rfqProjectId || !sub.supplierOrgId) continue;
    const project = projectById.get(sub.rfqProjectId);
    if (!project) continue;
    const category = awardCategory(sub.workType) ?? awardCategory(project.workType);
    if (!category) continue;
    const amount = submissionQuoteTotal(sub);
    if (amount == null || amount <= 0) continue;
    const key = `${sub.rfqProjectId}:${sub.supplierOrgId}:${category}`;
    const group = groups.get(key) ?? { category, project, orgId: sub.supplierOrgId, subs: [] };
    group.subs.push(sub);
    groups.set(key, group);
  }

  const slices: AwardedQuoteForecastSlice[] = [];
  const omitBillIds: string[] = [];
  for (const group of Array.from(groups.values())) {
    const bills = input.supplierBills.filter((bill) =>
      coversAward(bill)
      && bill.rfqProjectId === group.project.id
      && bill.supplierOrgId === group.orgId
      && bill.category === group.category,
    );
    const owned = new Set<string>();
    for (const sub of group.subs) {
      const quoteTotal = submissionQuoteTotal(sub) ?? 0;
      const own = bills.filter((bill) => (bill.rfqSubmissionId || '').trim() === sub.id);
      const covering = own.find((bill) =>
        amountsMatch(Number(bill.amount) || 0, quoteTotal) || liveAwardBillCoversQuote(bill),
      );
      if (covering) {
        owned.add(sub.id!);
        continue;
      }
      for (const bill of own) {
        if (bill.id && bill.status !== 'paid') omitBillIds.push(bill.id);
      }
    }
    const uncovered = group.subs.filter((sub) => !owned.has(sub.id!));
    if (!uncovered.length) continue;

    const orgBills = bills.filter((bill) => !isStructureSubmissionKey(bill.rfqSubmissionId));
    const uncoveredTotal = uncovered.reduce((sum, sub) => sum + (submissionQuoteTotal(sub) ?? 0), 0);
    const orgTotal = orgBills.reduce((sum, bill) => sum + (Number(bill.amount) || 0), 0);
    if (orgBills.length > 0 && amountsMatch(orgTotal, uncoveredTotal)) continue;

    const pool = orgBills.slice();
    const still: RfqSubmission[] = [];
    const byAmount = uncovered.slice().sort(
      (a, b) => (submissionQuoteTotal(b) ?? 0) - (submissionQuoteTotal(a) ?? 0),
    );
    for (const sub of byAmount) {
      const total = submissionQuoteTotal(sub) ?? 0;
      const idx = pool.findIndex((bill) => amountsMatch(Number(bill.amount) || 0, total));
      if (idx >= 0) pool.splice(idx, 1);
      else still.push(sub);
    }
    const stillTotal = still.reduce((sum, sub) => sum + (submissionQuoteTotal(sub) ?? 0), 0);
    const poolTotal = pool.reduce((sum, bill) => sum + (Number(bill.amount) || 0), 0);
    if (pool.length > 0 && amountsMatch(poolTotal, stillTotal)) continue;

    const track = group.category === 'cnc' ? 'CNC' : 'Timber';
    const vendorName = vendorByOrg.get(group.orgId) || (group.category === 'cnc' ? 'CNC supplier' : 'Timber supplier');
    for (const sub of still) {
      const names = (sub.quotedStructures ?? []).map((row) => (row.name || '').trim()).filter(Boolean);
      const dealId = sub.dealId || group.project.dealId;
      if (!dealId) continue;
      slices.push({
        submissionId: sub.id!,
        amount: Math.round(submissionQuoteTotal(sub) ?? 0),
        vendorName,
        label: names.length ? `${track} quote — ${names.join(', ')}` : `${track} quote`,
        dealId,
        projectId: group.project.timberProjectId,
        structureNames: names,
      });
    }
  }

  return { slices, omitBillIds };
}
